import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MapViewComponent } from '../../../shared/map-view/map-view.component';
import { Comunicado } from '../../../services/comunicado.service';
import { forkJoin, Subscription, interval, of, Subject } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import { DespachosService, DespachoApi, ClaveDespachoApi } from '../../../services/despachos.service';
import { SSEService } from '../../../services/sse.service';

/** UI model (lo que muestra la tarjeta de despachos) */
export interface DespachoUI {
  id: number;
  codigo: string;       // desde la clave (ej: 10-3-1)
  categoria: string;    // desde la clave (ej: "Rescate vehicular")
  direccion: string;   // ← solo calle + número
  comuna: string;
  coordenadas: { lat: number; lng: number }; // usa lat/lng del backend (geocoding)
  carros_asignados: string[];                // códigos de carros (ej: ["R-2", "B-2"])
  hora_despacho: Date;
  estado: 'activo' | 'en_ruta' | 'en_escena' | 'finalizado';
  prioridad: 'alta' | 'media' | 'baja';
  tieneGeocodificacion: boolean;             // indica si tiene coordenadas válidas
  geocode_status?: string;                   // estado del geocoding
}

@Component({
  standalone: true,
  selector: 'app-panel-comunicados',
  templateUrl: './panel-comunicados.component.html',
  styleUrls: ['./panel-comunicados.component.scss'],
  imports: [CommonModule, MapViewComponent]
})
export class PanelComunicadosComponent implements OnInit, OnDestroy {
  /* ====== Entradas desde el padre (solo comunicados) ====== */
  @Input() comunicados: Comunicado[] = [];
  @Input() comunicadosWidget: any = {};
  @Output() toggleEvent = new EventEmitter<void>();

  /* ====== Estado interno de despachos ====== */
  cargandoDespachos = false;
  despachos: DespachoUI[] = [];
  private idx = 0;
  private rotacionSub?: Subscription;
  private refreshSub?: Subscription;
  private destroy$ = new Subject<void>();

  /** Configuración */
  private readonly ROTACION_MS = 7000;
  private readonly AUTO_REFRESH_MS = 60000; // puedes subir/bajar este valor

  // Coordenadas por defecto (Centro de Santiago, Chile)
  private readonly DEFAULT_LAT = -33.4489;
  private readonly DEFAULT_LNG = -70.6693;

  constructor(
    private despachosSrv: DespachosService,
    private sse: SSEService
  ) {}

  ngOnInit(): void {
    this.cargarDespachosDesdeApi();
    this.iniciarAutoRefresh();

    // Escuchar SSE relevantes y refrescar (remueve fantasmas al instante)
    this.sse.events$
      .pipe(takeUntil(this.destroy$))
      .subscribe((ev: any) => {
        switch (ev?.type) {
          case 'nuevo_despacho':
          case 'despacho_actualizado':
          case 'despacho_cerrado':
          case 'despacho_eliminado':
            this.cargarDespachosDesdeApi();
            break;
        }
      });
  }

  ngOnDestroy(): void {
    this.detenerRotacion();
    if (this.refreshSub) { this.refreshSub.unsubscribe(); this.refreshSub = undefined; }
    this.destroy$.next(); this.destroy$.complete();
  }

  /* =========================================================
   * Cargar despachos y "hidratar" con código/categoría de la clave
   * ========================================================= */
  cargarDespachosDesdeApi(): void {
    this.cargandoDespachos = true;
    this.detenerRotacion();

    // Usa el servicio que ya filtra activos y no borrados
    this.despachosSrv.obtenerDespachosActivos().pipe(takeUntil(this.destroy$)).subscribe({
      next: (activos: DespachoApi[]) => {
        if (!activos || activos.length === 0) {
          this.despachos = [];
          this.cargandoDespachos = false;
          return;
        }

        // ids de clave únicos
        const ids = Array.from(new Set(activos.map(d => Number(d.clave_despacho_id)).filter(Boolean)));

        const procesar = (dic: Map<number, ClaveDespachoApi>) => {
          // mapear a UI
          this.despachos = activos.map<DespachoUI>((d) => {
            const clave = dic.get(Number(d.clave_despacho_id));

            // Geocoding
            const lat = d.lat;
            const lng = d.lng;
            const geocodeStatus = d.geocode_status;
            const tieneCoordenadasValidas = this.validarCoordenadas(lat, lng);

            const coordenadas = tieneCoordenadasValidas
              ? { lat: lat as number, lng: lng as number }
              : { lat: this.DEFAULT_LAT, lng: this.DEFAULT_LNG };

            // direccion => "direccion, comuna" usando direccion_normalizada (si existe)
            let direccionUI = d.direccion;
            let comunaUI = '';
            const dn = (d as any).direccion_normalizada as string | undefined;
            if (dn && typeof dn === 'string') {
              const partes = dn.split(',').map(s => s.trim());
              if (partes[0]) direccionUI = partes[0];     // "Ignacio Serrano 311"
              if (partes[1]) comunaUI = partes[1];        // "El Monte"
            }

            const despachoUI: DespachoUI = {
              id: d.id,
              codigo: clave?.codigo ?? `#${d.clave_despacho_id}`,
              categoria: clave?.categoria ?? 'Servicio',
              direccion: direccionUI,
              comuna: comunaUI,
              coordenadas,
              carros_asignados: [],
              hora_despacho: new Date(d.fecha_hora_despacho),
              estado: 'activo',
              prioridad: 'media',
              tieneGeocodificacion: tieneCoordenadasValidas,
              geocode_status: geocodeStatus || (tieneCoordenadasValidas ? 'OK' : 'pending')
            };

            // 🚒 Hidratar carros asignados: solo el "codigo"
            this.despachosSrv.obtenerCarrosAsignados(d.id).pipe(takeUntil(this.destroy$)).subscribe({
              next: (carros) => {
                despachoUI.carros_asignados = (carros || []).map((c: any) => c.codigo);
              },
              error: () => {
                despachoUI.carros_asignados = [];
              }
            });

            return despachoUI;
          }).sort((a, b) => b.hora_despacho.getTime() - a.hora_despacho.getTime());

          // arrancar rotación 7s si hay más de 1
          this.idx = 0;
          this.iniciarRotacion(this.ROTACION_MS);
          this.cargandoDespachos = false;
        };

        if (ids.length === 0) {
          // no hay claves; procesar con diccionario vacío
          procesar(new Map());
          return;
        }

        // pedir cada clave por id y armar diccionario id->clave
        const reqs = ids.map(id =>
          this.despachosSrv.obtenerClavePorId(id).pipe(catchError(() => of(null as unknown as ClaveDespachoApi)))
        );

        forkJoin(reqs).pipe(takeUntil(this.destroy$)).subscribe({
          next: (claves) => {
            const dic = new Map<number, ClaveDespachoApi>();
            claves.forEach((k, i) => { if (k) dic.set(ids[i], k); });
            procesar(dic);
          },
          error: () => {
            // si falla la carga de claves, al menos mostramos los despachos
            procesar(new Map());
          }
        });
      },
      error: () => {
        this.despachos = [];
        this.cargandoDespachos = false;
      }
    });
  }

  /**
   * Valida si las coordenadas son útiles para mostrar en el mapa
   */
  private validarCoordenadas(lat: any, lng: any): boolean {
    return typeof lat === 'number' &&
           typeof lng === 'number' &&
           !isNaN(lat) &&
           !isNaN(lng) &&
           lat !== 0 &&
           lng !== 0 &&
           lat >= -90 &&
           lat <= 90 &&
           lng >= -180 &&
           lng <= 180;
  }

  /* ================= Auto-refresh ================= */
  private iniciarAutoRefresh() {
    if (this.refreshSub) this.refreshSub.unsubscribe();
    this.refreshSub = interval(this.AUTO_REFRESH_MS)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.cargarDespachosDesdeApi());
  }

  /* ================= Rotación ================= */

  private iniciarRotacion(ms: number) {
    if (!this.despachos || this.despachos.length <= 1) return;
    this.rotacionSub = interval(ms)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.idx = (this.idx + 1) % this.despachos.length;
      });
  }

  private detenerRotacion() {
    if (this.rotacionSub) { this.rotacionSub.unsubscribe(); this.rotacionSub = undefined; }
  }

  getDespachoVisible(): DespachoUI | null {
    if (!this.despachos || this.despachos.length === 0) return null;
    return this.despachos[this.idx];
  }

  /**
   * Verifica si el despacho actual tiene geocodificación válida
   */
  despachoTieneGeocodificacion(): boolean {
    const despacho = this.getDespachoVisible();
    return despacho?.tieneGeocodificacion ?? false;
  }

  /**
   * Obtiene el estado del geocoding para mostrar en la UI
   */
  getEstadoGeocodificacion(): string {
    const despacho = this.getDespachoVisible();
    if (!despacho) return '';

    switch (despacho.geocode_status) {
      case 'OK': return despacho.tieneGeocodificacion ? 'Ubicación exacta' : 'Ubicación aproximada';
      case 'pending': return 'Obteniendo ubicación...';
      default: return 'Ubicación aproximada';
    }
  }

  /**
   * Solicita geocodificación para un despacho específico
   */
  solicitarGeocodificacion(despachoId: number): void {
    this.despachosSrv.solicitarGeocodificacion(despachoId).subscribe({
      next: () => this.cargarDespachosDesdeApi(),
      error: (error) => console.error('Error en geocodificación:', error)
    });
  }

  /* =================== UI helpers (comunicados) =================== */

  toggleComunicados(): void { this.toggleEvent.emit(); }

  get contadorComunicados(): number { return this.comunicados.length; }

  get hayUrgentes(): boolean {
    return this.comunicados.some(c => c.tipo?.toLowerCase() === 'urgente');
  }

  getComunicadosVisibles(): Comunicado[] {
    const inicio = this.comunicadosWidget?.indiceActual ?? 0;
    return this.comunicados.slice(inicio, inicio + 1);
  }

  trackByComunicado(index: number, comunicado: Comunicado): number {
    return comunicado.id || index;
  }

  getClaseTipoComunicado(tipo: string): string {
    switch ((tipo || '').toLowerCase()) {
      case 'urgente': return 'comunicado-urgente';
      case 'informativo': return 'comunicado-informativo';
      default: return 'comunicado-normal';
    }
  }

  formatearFechaComunicado(comunicado: Comunicado): string {
    const fecha = new Date(comunicado.fecha_publicacion || comunicado.creado_en || '');
    const ahora = new Date();
    const diffMs = ahora.getTime() - fecha.getTime();
    const diffHoras = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDias = Math.floor(diffHoras / 24);
    if (diffHoras < 1) return 'Hace menos de 1 hora';
    if (diffHoras < 24) return `Hace ${diffHoras} hora${diffHoras !== 1 ? 's' : ''}`;
    if (diffDias < 7) return `Hace ${diffDias} día${diffDias !== 1 ? 's' : ''}`;
    return fecha.toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: '2-digit' });
  }

  truncarContenido(contenido: string, limite: number = 100): string {
    if (!contenido) return '';
    return contenido.length <= limite ? contenido : contenido.substring(0, limite).trim() + '...';
  }

  /* =================== UI helpers (despachos) =================== */

  getTiempoTranscurrido(fecha: Date): string {
    if (!fecha) return '';
    const diffMin = Math.floor((Date.now() - fecha.getTime()) / 60000);
    if (diffMin < 1) return 'Recién despachado';
    if (diffMin < 60) return `${diffMin} min`;
    const h = Math.floor(diffMin / 60);
    return `${h}h ${diffMin % 60}min`;
  }

  /** Clases CSS según categoría (usamos palabras clave) */
  getClaseTipoDespacho(categoria: string): string {
    const c = (categoria || '').toLowerCase();
    if (c.includes('estructural') || c.includes('incendio') || c.includes('fuego')) return 'despacho-incendio';
    if (c.includes('rescate') && !c.includes('vehicular')) return 'despacho-rescate';
    if (c.includes('vehicular') || c.includes('accidente')) return 'despacho-accidente';
    if (c.includes('materiales peligrosos') || c.includes('hazmat')) return 'despacho-hazmat';
    if (c.includes('gas')) return 'despacho-gas';
    if (c.includes('eléctrico') || c.includes('electrico')) return 'despacho-electrico';
    return 'despacho-servicio';
  }

  getClaseEstadoDespacho(estado: string): string {
    switch (estado) {
      case 'activo': return 'estado-activo';
      case 'en_ruta': return 'estado-en-ruta';
      case 'en_escena': return 'estado-en-escena';
      case 'finalizado': return 'estado-finalizado';
      default: return 'estado-normal';
    }
  }

  getClasePrioridadDespacho(prioridad: string): string {
    switch (prioridad) {
      case 'alta': return 'prioridad-alta';
      case 'media': return 'prioridad-media';
      case 'baja': return 'prioridad-baja';
      default: return 'prioridad-normal';
    }
  }
}
